import { useRoutes } from "react-router";

import { Lazy } from "@shared/components/ui/lazy";
import { getAgendaRoutes } from "@shared/features/agenda/routes";
import { useApp } from "@shared/features/app/hooks/use-app";
import { getCommonAppRoutes } from "@shared/features/app/routes";
import { getAuthRoutes } from "@shared/features/auth/routes";
import { getPatientManagementRoutes } from "@shared/features/medicalRecord/routes";
import { getOrganizationRoutes } from "@shared/features/organization/routes";
import { getTeleexpertiseRoutes } from "@shared/features/teleexpertise/routes";
import { Sentry } from "@shared/utils/init-tracking";

const NotFound = Lazy(() => import("@shared/components/not-found"), "NotFound");
const AppRoot = Lazy(() => import("@shared/features/app/pages/app-root"), "AppRoot");
const Root = Lazy(() => import("@shared/features/app/pages/root"), "Root");
const JoinAppointment = Lazy(() => import("@shared/features/appointment/pages/join-appointment"), "JoinAppointment");
const AppointmentAsnyc = Lazy(() => import("@shared/features/appointment-async/pages/appointment-async"), "AppointmentAsnyc");
const AppointmentAsnycList = Lazy(() => import("@shared/features/appointment-async/pages/appointment-async-list"), "AppointmentAsnycList");
const CookiePolicy = Lazy(() => import("@shared/features/legals/cookie-policy"), "CookiePolicy");
const PrivacyPolicy = Lazy(() => import("@shared/features/legals/privacy-policy"), "PrivacyPolicy");
const LegalLayout = Lazy(() => import("@shared/features/legals/legals-layout"), "LegalLayout");
const PendingActivation = Lazy(() => import("@shared/features/newUser/pages/pending-activation"), "PendingActivation");
const PatientCheckUp = Lazy(() => import("@shared/features/patient-check-up/checkup"), "PatientCheckUp");
const PatientCheckUpFinal = Lazy(() => import("@shared/features/patient-check-up/checkup-final"), "PatientCheckUpFinal");
const RessourceGuide2 = Lazy(() => import("@shared/features/ressources/pages/ressource-guide-v2"), "RessourceGuide2");
const RessourcesList = Lazy(() => import("@shared/features/ressources/pages/ressources-list"), "RessourcesList");
const ServiceRequestTour = Lazy(() => import("@shared/features/service-request-tour/pages/service-request-tour"), "ServiceRequestTour");
const JoinOrganization = Lazy(() => import("@shared/features/organization/pages/join-organization"), "JoinOrganization");
const Appointment = Lazy(() => import("./pages/app/Appointment"), "Appointment");
const NewUser = Lazy(() => import("./pages/new-user"), "NewUser");
const WizardPEC = Lazy(() => import("./pages/app/organization/WizardPEC"), "WizardPEC");

const AppBase: React.FC = () => {
  const { initialized } = useApp();

  const element = useRoutes([
    { path: "/", element: <Root /> },
    ...getAuthRoutes(),
    { path: "/new-user", element: <NewUser /> },
    { path: "/wizardPEC", element: <WizardPEC /> },
    { path: "/pending-activation", element: <PendingActivation /> },
    {
      path: "/app",
      element: <AppRoot />,
      children: [
        ...getCommonAppRoutes(),
        { path: "join-appointment/:id", element: <JoinAppointment /> },
        {
          path: "appointment/async",
          children: [
            { path: ":id", element: <AppointmentAsnyc /> },
            { index: true, element: <AppointmentAsnycList /> },
          ],
        },
        { path: "appointment/:id", element: <Appointment /> },
        ...getAgendaRoutes(),
        ...getPatientManagementRoutes(),
        ...getTeleexpertiseRoutes(),
        { path: "join-organization", element: <JoinOrganization /> },
        ...getOrganizationRoutes(),
        { path: "service-request-tour", element: <ServiceRequestTour /> },
        {
          path: "ressources",
          children: [
            { index: true, element: <RessourcesList /> },
            { path: ":slug", element: <RessourceGuide2 /> },
          ],
        },
      ],
    },
    { path: "patient-check-up", element: <PatientCheckUp /> },
    { path: "/patient-check-up-final", element: <PatientCheckUpFinal /> },
    {
      path: "legals",
      element: <LegalLayout />,
      children: [
        { path: "cookie-policy", element: <CookiePolicy /> },
        { path: "privacy-policy", element: <PrivacyPolicy /> },
      ],
    },
    { path: "*", element: <NotFound /> },
  ]);

  return <>{initialized ? element : null}</>;
};

export const App = Sentry.withProfiler(AppBase);
